/**
 * Smart Match Flow
 * 
 * Workflow 1: System recommends caregivers, Agency reviews and decides.
 * 
 * Shows ranked matches with detailed compatibility scores and reasons.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Target, Search, Users, CheckCircle, XCircle,
  AlertTriangle, Clock, MapPin, Award, Heart, Languages,
  ChevronDown, ChevronUp, Send, User
} from "lucide-react";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SmartMatchFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get("client");
  
  const [step, setStep] = useState(1); // 1: Select Client, 2: View Matches, 3: Send Offer
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Offer modal
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offerDetails, setOfferDetails] = useState({
    isPrimary: false,
    effectiveDate: new Date().toISOString().split("T")[0],
    notes: "",
    expirationHours: "48_hours",
  });
  const [validationResult, setValidationResult] = useState(null);
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (preselectedClientId && clients.length > 0) {
      const client = clients.find(c => c.id === preselectedClientId);
      if (client) {
        handleSelectClient(client);
      }
    }
  }, [preselectedClientId, clients]);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/clients?is_active=true`, {
        credentials: "include",
      });
      if (res.ok) {
        setClients(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const handleSelectClient = async (client) => {
    setSelectedClient(client);
    setStep(2);
    await findMatches(client.id);
  };

  const findMatches = async (clientId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/assignment-center/smart-match`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: clientId,
          max_results: 20,
        }),
      });
      
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
        if (data.matches?.length > 0) {
          setExpandedMatch(data.matches[0].caregiver_id);
        }
      }
    } catch (err) {
      console.error("Failed to find matches:", err);
      toast.error("Failed to find matches");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMatch = (match) => {
    setSelectedMatch(match);
    setShowOfferModal(true);
    setValidationResult(null);
    setShowOverrideWarning(false);
  };

  const handleSendOffer = async (overrideWarnings = false) => {
    if (!selectedMatch || !selectedClient) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/assignment-center/offer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: selectedClient.id,
          caregiver_id: selectedMatch.caregiver_id,
          assignment_type: "smart_match",
          match_score: selectedMatch.match_score,
          is_primary: offerDetails.isPrimary,
          effective_date: offerDetails.effectiveDate,
          notes: offerDetails.notes,
          expiration_type: offerDetails.expirationHours,
          override_warnings: overrideWarnings,
          override_reason: overrideWarnings ? "Agency authorized override" : null,
        }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        if (data.status === "offer_sent") {
          toast.success(`Offer sent to ${selectedMatch.caregiver_name}`);
          setShowOfferModal(false);
          navigate("/assignment-center");
        } else if (data.status === "validation_warnings") {
          setValidationResult(data.validation);
          setShowOverrideWarning(true);
        }
      } else {
        toast.error(data.detail?.message || data.detail || "Failed to send offer");
        if (data.detail?.validation) {
          setValidationResult(data.detail.validation);
        }
      }
    } catch (err) {
      console.error("Failed to send offer:", err);
      toast.error("Failed to send offer");
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    return name.includes(query);
  });

  const eligibleMatches = matches.filter(m => m.is_eligible);
  const ineligibleMatches = matches.filter(m => !m.is_eligible);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="smart-match-flow">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/assignment-center")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Target className="w-6 h-6 text-primary" />
              Smart Match
            </h1>
            <p className="text-muted-foreground">
              Find the best caregiver match for your client
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {[
            { num: 1, label: "Select Client" },
            { num: 2, label: "Review Matches" },
            { num: 3, label: "Send Offer" },
          ].map(({ num, label }) => (
            <div key={num} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= num ? "bg-primary text-white" : "bg-muted text-muted-foreground"
              }`}>
                {step > num ? <CheckCircle className="w-5 h-5" /> : num}
              </div>
              <span className={`text-sm ${step >= num ? "font-medium" : "text-muted-foreground"}`}>
                {label}
              </span>
              {num < 3 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        {/* Step 1: Select Client */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>Select a Client</CardTitle>
              <CardDescription>
                Choose the client who needs a caregiver assigned
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search clients..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="client-search"
                />
              </div>
              
              {filteredClients.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No active clients found
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <div
                      key={client.id}
                      className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => handleSelectClient(client)}
                      data-testid={`client-option-${client.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
                          {(client.first_name || "?").charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium">
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {client.city}, {client.state}
                          </p>
                        </div>
                      </div>
                      <ChevronDown className="w-5 h-5 text-muted-foreground rotate-[-90deg]" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: View Matches */}
        {step === 2 && (
          <>
            {/* Selected Client Summary */}
            {selectedClient && (
              <Card className="bg-gradient-to-r from-slate-50 to-white">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold">
                        {(selectedClient.first_name || "?").charAt(0)}
                      </div>
                      <div>
                        <p className="font-semibold">
                          {selectedClient.first_name} {selectedClient.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedClient.city}, {selectedClient.state} {selectedClient.zip_code}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setStep(1)}>
                      Change Client
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
                <p className="text-muted-foreground">Finding best matches...</p>
              </div>
            ) : (
              <>
                {/* Eligible Matches */}
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Eligible Caregivers ({eligibleMatches.length})
                  </h3>
                  
                  {eligibleMatches.length === 0 ? (
                    <Card>
                      <CardContent className="py-8 text-center text-muted-foreground">
                        No eligible caregivers found. Try adjusting requirements.
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="space-y-3">
                      {eligibleMatches.map((match, index) => (
                        <MatchCard
                          key={match.caregiver_id}
                          match={match}
                          rank={index + 1}
                          isExpanded={expandedMatch === match.caregiver_id}
                          onToggle={() => setExpandedMatch(
                            expandedMatch === match.caregiver_id ? null : match.caregiver_id
                          )}
                          onSelect={() => handleSelectMatch(match)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Ineligible Matches */}
                {ineligibleMatches.length > 0 && (
                  <div className="mt-6">
                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-muted-foreground">
                      <XCircle className="w-5 h-5 text-red-500" />
                      Ineligible ({ineligibleMatches.length})
                    </h3>
                    <div className="space-y-2 opacity-60">
                      {ineligibleMatches.slice(0, 5).map((match) => (
                        <Card key={match.caregiver_id}>
                          <CardContent className="py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <User className="w-8 h-8 text-muted-foreground" />
                                <div>
                                  <p className="font-medium">{match.caregiver_name}</p>
                                  <p className="text-sm text-red-600">
                                    {match.disqualifying_reasons?.[0] || "Not eligible"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* Offer Modal */}
        <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Send Assignment Offer</DialogTitle>
              <DialogDescription>
                Review and send offer to {selectedMatch?.caregiver_name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {selectedMatch && (
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-bold">
                    {selectedMatch.match_percentage}%
                  </div>
                  <div>
                    <p className="font-medium">{selectedMatch.caregiver_name}</p>
                    <p className="text-sm text-muted-foreground">
                      Match Score: {selectedMatch.match_score}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Validation Warnings */}
              {validationResult?.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-800 font-medium mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Validation Warnings
                  </div>
                  <ul className="text-sm space-y-1 text-amber-700">
                    {validationResult.warnings.map((w, i) => (
                      <li key={i}>• {w.message}</li>
                    ))}
                  </ul>
                  {showOverrideWarning && (
                    <p className="text-sm mt-2 text-amber-800">
                      As an authorized representative, you may override these warnings.
                    </p>
                  )}
                </div>
              )}
              
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isPrimary"
                    checked={offerDetails.isPrimary}
                    onCheckedChange={(checked) => 
                      setOfferDetails(prev => ({ ...prev, isPrimary: checked }))
                    }
                  />
                  <Label htmlFor="isPrimary">Set as Primary Caregiver</Label>
                </div>
                
                <div>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={offerDetails.effectiveDate}
                    onChange={(e) => 
                      setOfferDetails(prev => ({ ...prev, effectiveDate: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Offer Expires In</Label>
                  <Select
                    value={offerDetails.expirationHours}
                    onValueChange={(value) => 
                      setOfferDetails(prev => ({ ...prev, expirationHours: value }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24_hours">24 Hours</SelectItem>
                      <SelectItem value="48_hours">48 Hours</SelectItem>
                      <SelectItem value="72_hours">72 Hours</SelectItem>
                      <SelectItem value="no_expiration">No Expiration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Notes (Optional)</Label>
                  <Textarea
                    value={offerDetails.notes}
                    onChange={(e) => 
                      setOfferDetails(prev => ({ ...prev, notes: e.target.value }))
                    }
                    placeholder="Any special instructions..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOfferModal(false)}>
                Cancel
              </Button>
              {showOverrideWarning ? (
                <Button onClick={() => handleSendOffer(true)} disabled={loading}>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Override & Send Offer
                </Button>
              ) : (
                <Button onClick={() => handleSendOffer(false)} disabled={loading}>
                  <Send className="w-4 h-4 mr-2" />
                  Send Offer
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
}

// Match Card Component
function MatchCard({ match, rank, isExpanded, onToggle, onSelect }) {
  return (
    <Card className={`transition-all ${isExpanded ? "shadow-md ring-1 ring-primary/20" : ""}`}>
      <CardContent className="p-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={onToggle}
        >
          <div className="flex items-center gap-4">
            {/* Rank */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
              rank === 1 ? "bg-yellow-100 text-yellow-700" :
              rank === 2 ? "bg-slate-100 text-slate-700" :
              rank === 3 ? "bg-orange-100 text-orange-700" :
              "bg-gray-100 text-gray-600"
            }`}>
              #{rank}
            </div>
            
            {/* Match Score */}
            <div className={`w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold ${
              match.match_percentage >= 80 ? "bg-green-100 text-green-700" :
              match.match_percentage >= 60 ? "bg-blue-100 text-blue-700" :
              "bg-amber-100 text-amber-700"
            }`}>
              {match.match_percentage}%
            </div>
            
            {/* Name & Quick Info */}
            <div>
              <p className="font-semibold">{match.caregiver_name}</p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {match.distance_miles !== null && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {match.distance_miles?.toFixed(1)} mi
                  </span>
                )}
                {match.weekly_hours_available !== null && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {match.weekly_hours_available}h avail
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onSelect(); }}>
              Select
            </Button>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t space-y-3">
            {/* Positive Reasons */}
            {match.positive_reasons?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-green-700 mb-1">Why Recommended</p>
                <div className="flex flex-wrap gap-1">
                  {match.positive_reasons.map((reason, i) => (
                    <Badge key={i} variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {reason}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Negative Reasons */}
            {match.negative_reasons?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-amber-700 mb-1">Considerations</p>
                <div className="flex flex-wrap gap-1">
                  {match.negative_reasons.map((reason, i) => (
                    <Badge key={i} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      {reason}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <div className="bg-slate-50 rounded p-2">
                <span className="text-muted-foreground">Available</span>
                <p className="font-medium">{match.is_available ? "Yes" : "No"}</p>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <span className="text-muted-foreground">Qualified</span>
                <p className="font-medium">{match.is_qualified ? "Yes" : "No"}</p>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <span className="text-muted-foreground">Capacity</span>
                <p className="font-medium">{match.has_capacity ? "Yes" : "Limited"}</p>
              </div>
              <div className="bg-slate-50 rounded p-2">
                <span className="text-muted-foreground">In Range</span>
                <p className="font-medium">{match.is_within_travel_range ? "Yes" : "Far"}</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
